import { useState } from 'react';
import type { DemandQuote } from '../types.ts';
import { QUOTE_DISPLAY_CAP, capList, formatDemandDate, orderQuotes, quoteJoinLabels } from '../lib/demand.ts';
import { Highlight } from './Highlight.tsx';

/**
 * Demand quotes as `.ev` blocks — the one quote list behind the axis detail, vendor friction and
 * the unassigned pile, so a quote reads the same wherever it surfaces. Each block is whole, per
 * the craft floor: the quote, then a mono `.src` line with the permalink and its provenance
 * (source, date, stance, author role, the product the thread named).
 *
 * Newest first, capped for display with a "show all" toggle; the data is never trimmed. A
 * vendor representative's words get the brass source chip, the same mark third-party evidence
 * carries elsewhere: a claim whose author has a stake, to be weighed before it is believed.
 *
 * A signal with more than one home says so at the end of the line ("joins: …", every one of
 * them), so a quote read under one axis is not mistaken for evidence about that axis alone.
 */
export function DemandQuoteList({
  quotes,
  highlightQuery = '',
  cap = QUOTE_DISPLAY_CAP,
}: {
  quotes: DemandQuote[];
  highlightQuery?: string;
  cap?: number;
}): JSX.Element {
  const [showAll, setShowAll] = useState(false);
  if (!quotes.length) return <p className="muted demand-quotes-empty">no quotes</p>;

  const { shown, hidden } = capList(orderQuotes(quotes), showAll, cap);
  return (
    <div className="demand-quotes">
      {shown.map((q, i) => {
        const joins = quoteJoinLabels(q);
        return (
          <div key={`${q.permalink}::${q.utterance_id}::${i}`} className="ev demand-quote">
            <q>
              <Highlight text={q.quote} query={highlightQuery} />
            </q>
            <span className="src">
              <a href={q.permalink} target="_blank" rel="noreferrer" className="bare">
                {q.permalink}
              </a>
              <span className="demand-quote-meta">
                {q.source} · {formatDemandDate(q.published_at) ?? 'undated'} · {q.stance}
                {q.author_role !== 'vendor_rep' && ` · ${q.author_role}`}
                {q.product && ` · re: ${q.product}`}
              </span>
              {q.author_role === 'vendor_rep' && (
                <span className="source-chip" title="Written by someone who works for a vendor — a voice with a stake">
                  vendor rep
                </span>
              )}
              {joins.length > 0 && <span className="demand-quote-joins">joins: {joins.join('; ')}</span>}
            </span>
          </div>
        );
      })}
      {(hidden > 0 || (showAll && quotes.length > cap)) && (
        <div className="expand-all-row">
          <button type="button" onClick={() => setShowAll((s) => !s)}>
            {showAll ? `show first ${cap}` : `show all ${quotes.length}`}
          </button>
        </div>
      )}
    </div>
  );
}
